import * as React from "react";
import { AlertCircle, LucideIcon, ShieldCheck } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button, Dialog } from "@/components/ui";
import { SocoAiLogo } from "@/components/Holo";

export interface HelpDialogProps {
  open: boolean;
  onOpenChange(open: boolean): void;
}

/** The three Design Studio guides, one tab each in the help dialog. */
export type HelpTopic = "how" | "ask" | "backgrounds";

/** A guide's body; `onNavigate` switches the dialog to another guide's tab. */
export interface HelpGuideProps {
  onNavigate(topic: HelpTopic): void;
}

export interface HelpDialogShellProps {
  open: boolean;
  onOpenChange(open: boolean): void;
  title: string;
  description?: string;
  size?: "md" | "lg" | "xl";
  /** The SocialCoach AI logo before the title; on by default. */
  showLogo?: boolean;
  children: React.ReactNode;
  /** Replaces the default Close button; `null` drops the footer and shows the corner close instead. */
  footer?: React.ReactNode;
}

/** Help copy marks product labels as **bold**; this turns those spans into <strong>. */
export function withBold(text: string): React.ReactNode {
  const parts = text.split(/\*\*(.+?)\*\*/);
  if (parts.length === 1) return text;
  return parts.map((part, i) => (i % 2 === 1 ? <strong key={i}>{part}</strong> : part));
}

export const HelpDialog: React.FC<HelpDialogShellProps> = ({
  open,
  onOpenChange,
  title,
  description,
  size = "lg",
  showLogo = true,
  children,
  footer,
}) => (
  <Dialog open={open} onOpenChange={next => onOpenChange(next)} size={size} showCloseButton={footer === null}>
    <Dialog.Header>
      <Dialog.Title className="flex items-center gap-2 text-xl font-bold font-sans">
        {showLogo && <SocoAiLogo variant="mono" height={16} />}
        <span>{title}</span>
      </Dialog.Title>
      {description && <Dialog.Description className="text-slate-500 text-sm">{description}</Dialog.Description>}
    </Dialog.Header>

    <Dialog.Content className="max-h-[70vh] overflow-y-auto space-y-6 py-4">{children}</Dialog.Content>

    {footer !== null && <Dialog.Footer>{footer ?? <HelpFooter onClose={() => onOpenChange(false)} />}</Dialog.Footer>}
  </Dialog>
);

/** Close, with a link to the customer guide when one is published. */
export const HelpFooter: React.FC<{ guideUrl?: string | null; onClose(): void }> = ({ guideUrl, onClose }) => (
  <>
    {guideUrl && (
      <Button
        variant="link"
        className="sm:mr-auto"
        nativeButton={false}
        render={<a href={guideUrl} target="_blank" rel="noopener noreferrer" />}
      >
        Read the full guide
      </Button>
    )}
    <Button onClick={onClose}>Close</Button>
  </>
);

function cardBody(body: React.ReactNode): React.ReactNode {
  return typeof body === "string" ? <p>{withBold(body)}</p> : <div>{body}</div>;
}

export const HelpCards: React.FC<{
  /** A string body may mark product labels with **bold**; anything richer renders as given. */
  items: { title: string; body: React.ReactNode }[];
  columns?: 2 | 3;
  numbered?: boolean;
}> = ({ items, columns, numbered }) => {
  const grid = cn("grid grid-cols-1 gap-4", columns === 2 && "md:grid-cols-2", columns === 3 && "md:grid-cols-3");
  const card = "bg-slate-50 border border-slate-200/50 p-4 rounded-xl leading-normal text-sm text-slate-600";

  if (numbered) {
    return (
      <ol className={grid}>
        {items.map((item, i) => (
          <li key={item.title} className={cn(card, "flex flex-col gap-2")}>
            <span className="flex size-7 items-center justify-center rounded-full bg-indigo-100 text-sm font-semibold text-primary">
              {i + 1}
            </span>
            <div className="font-semibold text-slate-800 text-base">{item.title}</div>
            {cardBody(item.body)}
          </li>
        ))}
      </ol>
    );
  }

  return (
    <div className={grid}>
      {items.map(item => (
        <div key={item.title} className={cn(card, "space-y-2")}>
          <div className="font-semibold text-slate-800 text-base">{item.title}</div>
          {cardBody(item.body)}
        </div>
      ))}
    </div>
  );
};

export const HelpCallout: React.FC<React.PropsWithChildren<{ icon?: LucideIcon }>> = ({
  icon: Icon = AlertCircle,
  children,
}) => (
  <div className="flex gap-3 p-4 bg-blue-50/50 border border-blue-100 rounded-xl text-sm text-blue-800 leading-normal">
    <Icon className="size-5 shrink-0 text-blue-500 mt-0.5" aria-hidden="true" />
    <p>{children}</p>
  </div>
);

export const HelpFaq: React.FC<{ items: { q: string; a: string }[] }> = ({ items }) => (
  <HelpSection title="Questions">
    <div className="grid grid-cols-1 gap-6 text-sm leading-normal text-slate-600">
      {items.map(item => (
        <div key={item.q} className="space-y-1">
          <span className="text-base font-bold text-slate-800 flex items-center gap-1">
            <ShieldCheck className="size-4 text-emerald-500 shrink-0" aria-hidden="true" />
            {item.q}
          </span>
          <p>{withBold(item.a)}</p>
        </div>
      ))}
    </div>
  </HelpSection>
);

export const HelpSection: React.FC<React.PropsWithChildren<{ title: string; icon?: LucideIcon }>> = ({
  title,
  icon: Icon,
  children,
}) => (
  <div className="space-y-3">
    <span className="text-base font-bold text-slate-900 flex items-center gap-1.5 select-none">
      {Icon && <Icon className="size-4 text-primary" aria-hidden="true" />}
      {title}
    </span>
    {children}
  </div>
);
